import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { ProjectConnectionGroupCreateRequest, ProjectConnectionGroupUpdateRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useAddProjectConnectionGroup = (connectionId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: ProjectConnectionGroupCreateRequest) =>
      api.postProjectConnectionsGroupsV1(connectionId, payload),
    onSuccess: () => {
      showFlashToast({
        variant: "success",
        message: t("page.project-connection-groups.create.success"),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_CONNECTIONS });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.project-connection-groups.create.error"),
      });
    },
  });
};

const useUpdateProjectConnectionGroup = (connectionId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ groupId, payload }: { groupId: string; payload: ProjectConnectionGroupUpdateRequest }) =>
      api.putProjectConnectionsGroupsV1(connectionId, groupId, payload),
    onSuccess: () => {
      showFlashToast({
        variant: "success",
        message: t("page.project-connection-groups.edit.success"),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_CONNECTIONS });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.project-connection-groups.edit.error"),
      });
    },
  });
};

const useDeleteProjectConnectionGroup = (connectionId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (groupId: string) => api.deleteProjectConnectionsGroupsByIdV1(connectionId, groupId),
    onSuccess: () => {
      showFlashToast({
        variant: "success",
        message: t("page.project-connection-groups.delete.success"),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_CONNECTIONS });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.project-connection-groups.delete.error"),
      });
    },
  });
};

export const projectConnectionGroupMutations = {
  useAddProjectConnectionGroup,
  useUpdateProjectConnectionGroup,
  useDeleteProjectConnectionGroup,
};
